import { createContext, use, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CefrLevel, Role } from '@shared/types/api'
import { api, ApiClientError } from './api'

export interface UserProfile {
  nativeLanguage: string
  targetLanguage: string
  currentLevel: CefrLevel
  travelDate: string | null
  timezone: string
  dailyGoalXp: number
  dailyGoalMin: number
  learningGoals: string[]
  onboardedAt: number | null
}

export interface CurrentUser {
  id: string
  displayName: string
  role: Role
  profile: UserProfile | null
}

interface AuthContextValue {
  user: CurrentUser | null
  isLoading: boolean
}

const AuthContext = createContext<AuthContextValue>({ user: null, isLoading: true })

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data, isPending } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      try {
        const res = await api<{ user: CurrentUser }>('/auth/me')
        return res.user
      } catch (error) {
        // A missing session is the normal signed-out state, not an error worth
        // surfacing or retrying.
        if (error instanceof ApiClientError && error.status === 401) return null
        throw error
      }
    },
    staleTime: 60_000,
    retry: false,
  })

  return (
    <AuthContext value={{ user: data ?? null, isLoading: isPending }}>{children}</AuthContext>
  )
}

export function useAuth(): AuthContextValue {
  return use(AuthContext)
}

export function useLogin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api<{ user: CurrentUser }>('/auth/login', { method: 'POST', body: input }),
    onSuccess: (data) => {
      queryClient.setQueryData(['auth', 'me'], data.user)
    },
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api<void>('/auth/logout', { method: 'POST' }),
    onSuccess: () => {
      // Order matters, and so does not using `clear()`.
      //
      // `queryClient.clear()` *removes* the auth query the mounted `useQuery`
      // is subscribed to. The observer is left holding its last result with
      // nothing to notify it, so the app keeps rendering the signed-in screen
      // until something forces a remount — which is why signing out appeared
      // to do nothing until the page was refreshed.
      //
      // Writing null first pushes every observer into the signed-out state
      // synchronously; the rest of the cache is then dropped by key, leaving
      // the auth query itself intact and subscribed.
      queryClient.setQueryData(['auth', 'me'], null)
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== 'auth',
      })
    },
  })
}
